/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia';
import { healthRepository } from '@/services/repositories/health.repository.js';

/**
 * @typedef {import('../models/health.model.js').HealthRecord} HealthRecord
 */

export const useHealthStore = defineStore('health', {
  state: () => ({
    /** @type {HealthRecord[]} */
    healthRecords: [],
    loading: false,
    error: null
  }),

  getters: {
    /**
     * Get wounds, diseases, and injuries for a specific goat
     * @returns {function(number): HealthRecord[]}
     */
    wounds: (state) => (goatId) => {
      return state.healthRecords
        .filter(record => 
          record.goatId === goatId && 
          ['wound', 'disease', 'injury'].includes(record.type)
        )
        .sort((a, b) => {
          const dateCompare = new Date(b.measuredAt) - new Date(a.measuredAt);
          if (dateCompare !== 0) return dateCompare;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },

    /**
     * Get treatments and vaccinations for a specific goat
     * @returns {function(number): HealthRecord[]}
     */
    treatments: (state) => (goatId) => {
      return state.healthRecords
        .filter(record => 
          record.goatId === goatId && 
          ['treatment', 'vaccination'].includes(record.type)
        )
        .sort((a, b) => {
          const dateCompare = new Date(b.measuredAt) - new Date(a.measuredAt);
          if (dateCompare !== 0) return dateCompare;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },

    /**
     * Get all health records for a specific goat
     * @returns {function(number): HealthRecord[]}
     */
    getByGoat: (state) => (goatId) => {
      return state.healthRecords
        .filter(record => record.goatId === goatId)
        .sort((a, b) => {
          const dateCompare = new Date(b.measuredAt) - new Date(a.measuredAt);
          if (dateCompare !== 0) return dateCompare;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    }
  },

  actions: {
    /**
     * Fetch all health records from database
     * @returns {Promise<void>}
     */
    async fetchHealthRecords() {
      this.loading = true;
      this.error = null;
      try {
        this.healthRecords = await healthRepository.findAll();
        //console.log(`Fetched ${this.healthRecords.length} health records`);
      } catch (error) {
        this.error = error.message;
        this.healthRecords = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch health records for a specific goat
     * @param {number} goatId
     * @returns {Promise<void>}
     */
    async fetchHealthRecordsByGoat(goatId) {
      this.loading = true;
      this.error = null;
      try {
        const records = await healthRepository.findByGoat(goatId);
        // Merge with existing records, avoiding duplicates
        const existingIds = new Set(this.healthRecords.map(r => r.id));
        const newRecords = records.filter(r => !existingIds.has(r.id));
        // Remove old records for this goat
        this.healthRecords = this.healthRecords.filter(r => r.goatId !== goatId);
        // Add fresh records
        this.healthRecords.push(...records);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create a new health record
     * @param {Omit<HealthRecord, 'id'|'createdAt'|'updatedAt'>} healthData
     * @returns {Promise<number>}
     */
    async createHealthRecord(healthData) {
      this.loading = true;
      this.error = null;
      try {
        const id = await healthRepository.create(healthData);
        // Refresh records for this goat
        await this.fetchHealthRecordsByGoat(healthData.goatId);
        return id;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update a health record
     * @param {number} id
     * @param {Partial<HealthRecord>} updates
     * @returns {Promise<void>}
     */
    async updateHealthRecord(id, updates) {
      this.loading = true;
      this.error = null;
      try {
        await healthRepository.update(id, updates);
        // Find and update in local state
        const index = this.healthRecords.findIndex(r => r.id === id);
        if (index !== -1) {
          const goatId = this.healthRecords[index].goatId;
          await this.fetchHealthRecordsByGoat(goatId);
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Delete a health record
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deleteHealthRecord(id) {
      this.loading = true;
      this.error = null;
      try {
        const record = this.healthRecords.find(r => r.id === id);
        await healthRepository.delete(id);
        // Remove from local state
        this.healthRecords = this.healthRecords.filter(r => r.id !== id);
        // Refresh if we know the goat ID
        if (record) {
          await this.fetchHealthRecordsByGoat(record.goatId);
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }
});
